import {
    Box, Stack,
} from '@chakra-ui/react';
import { faInfoCircle } from '@fortawesome/sharp-light-svg-icons';
import { useMemo } from 'react';
import { PropertiesStats } from './properties-stats';
import { useActiveIntegrations } from '../../../integration';
import { CardLayout } from '../card-layout';
import { PortfolioPopoverContent } from './portfolio-popover-content';
import { useGetStats } from '../../hooks/use-get-stats';
import { ActionsRequiredPopoverContent } from './actions-required-popover-content';
import { ActionsRequiredGraph } from './actions-required-graph';
import { PortfolioGraph } from './portfolio-graph';
import { usePropertyManager } from '../../../agency';

export function DashboardGraphs({ propertyManagerId }: { propertyManagerId?: string | null }) {
    const { isIntegrated } = useActiveIntegrations();
    const { data } = useGetStats(propertyManagerId);
    const {
        propertyManager,
    } = usePropertyManager(propertyManagerId ?? null);

    const totalPropertiesOnService = useMemo(() => {
        const hideWhenIntegrated = isIntegrated && propertyManagerId === null;
        if (data === undefined || hideWhenIntegrated) {
            return null;
        }

        if (propertyManagerId === null || propertyManagerId === undefined) {
            return `${data.portfolioStats.MANAGED_BY_DI.toLocaleString()} properties on service`;
        }

        // Calculate the total properties on service filtered by a property manager
        // by looking at the sum of the filtered parts
        const propertyManagerPortfolio = data.portfolioStats.BOOKING_IN_PROGRESS
            + data.portfolioStats.COMPLIANT
            + data.portfolioStats.UNRESOLVED_ACTIVE_NOTICES
            + data.portfolioStats.UNPAID_OVERDUE_INVOICES
            + data.portfolioStats.NON_COMPLIANT_QUOTATIONS;

        return `${propertyManagerPortfolio.toLocaleString()} of ${data.portfolioStats.MANAGED_BY_DI.toLocaleString()} total properties on service`;
    }, [data, propertyManagerId, isIntegrated]);

    return (
        <>
            { isIntegrated && (<PropertiesStats />) }

            <CardLayout px={{ base: 6, lg: 0 }} py={0}>
                <Stack
                    direction={{ base: 'column', lg: 'row' }}
                    w="full"
                    gap={{ base: 4, md: 8, lg: 4 }}
                    justifyContent="space-between"
                    alignItems="stretch"
                    flexWrap={{ base: 'wrap', lg: 'nowrap' }}
                >
                    {/* Portfolio */}
                    <CardLayout bgColor="white">
                        <CardLayout.Title>
                            <CardLayout.Title.Heading
                                heading={`Portfolio ${propertyManager !== null ? `for ${propertyManager.name}` : ''}`}
                                position="relative"
                            >
                                {
                                    (totalPropertiesOnService !== null && (
                                        <Box alignSelf="start" color="muted.500" position="absolute" top={6}>
                                            {totalPropertiesOnService}
                                        </Box>
                                    ))
                                }
                            </CardLayout.Title.Heading>
                            <CardLayout.Title.Popover popoverIcon={faInfoCircle}>
                                <PortfolioPopoverContent />
                            </CardLayout.Title.Popover>
                        </CardLayout.Title>
                        <CardLayout.Body>
                            <PortfolioGraph propertyManagerId={propertyManagerId} />
                        </CardLayout.Body>
                    </CardLayout>

                    {/* Actions required */}
                    <CardLayout bgColor="white">
                        <CardLayout.Title>
                            <CardLayout.Title.Heading
                                heading={`Actions required ${propertyManager !== null ? `for ${propertyManager.name}` : ''}`}
                            />
                            <CardLayout.Title.Popover popoverIcon={faInfoCircle}>
                                <ActionsRequiredPopoverContent />
                            </CardLayout.Title.Popover>
                        </CardLayout.Title>
                        <CardLayout.Body>
                            <ActionsRequiredGraph propertyManagerId={propertyManagerId} />
                        </CardLayout.Body>
                    </CardLayout>
                </Stack>
            </CardLayout>
        </>
    );
}
